import authService from './auth.service';

const BASE_URL = process.env.REACT_APP_SESSION_API_URL || 'http://localhost:3005';

const getOrgId = () => {
    try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const orgField = userData?.data?.organization_id;
        // organization_id may be a plain string or an object with ._id
        return (typeof orgField === 'object' && orgField !== null) ? orgField._id : orgField;
    } catch (_) {
        return null;
    }
};

const getHeaders = () => {
    const orgId = getOrgId();
    return {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
        ...(orgId && { 'X-Org-Id': orgId }),
    };
};

const parseResponse = async (response) => {
    if (!response.ok) {
        const text = await response.text();
        let message = `HTTP ${response.status}`;
        try { message = JSON.parse(text)?.error?.message || message; } catch (_) {}
        throw new Error(message);
    }
    return response.json();
};

const request = async (method, path, body) => {
    const fetchOptions = {
        method,
        headers: getHeaders(),
        mode: 'cors',
        ...(body !== undefined && { body: JSON.stringify(body) }),
    };
    const response = await fetch(`${BASE_URL}${path}`, fetchOptions);

    if (response.status === 401 || response.status === 403) {
        const refreshed = await authService.refreshToken();
        if (!refreshed) {
            authService.logout();
            throw new Error('Authentication failed');
        }
        const retryResponse = await fetch(`${BASE_URL}${path}`, {
            ...fetchOptions,
            headers: getHeaders(),
        });
        return parseResponse(retryResponse);
    }

    return parseResponse(response);
};

// List caller's own sessions
const listMySessions = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/v1/sessions${qs ? `?${qs}` : ''}`);
};

// List all org sessions (admin/supervisor)
const listOrgSessions = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/v1/sessions/org${qs ? `?${qs}` : ''}`);
};

// Get single session by sessionId
const getSessionById = (sessionId) =>
    request('GET', `/v1/sessions/${sessionId}`);

// Create session
const createSession = (data) =>
    request('POST', '/v1/sessions', data);

// Update session (partial)
const updateSession = (sessionId, data) =>
    request('PATCH', `/v1/sessions/${sessionId}`, data);

// Soft-delete session
const deleteSession = (sessionId) =>
    request('DELETE', `/v1/sessions/${sessionId}`);

// Load / switch to a session
const loadSession = (sessionId) =>
    request('POST', `/v1/sessions/${sessionId}/load`);

// Unlock a locked session (supervisor only)
const unlockSession = (sessionId) =>
    request('POST', `/v1/sessions/${sessionId}/unlock`);

// ─── Short URL Alias Pool ───

// List short URL aliases; pass { available: true } to get only unassigned ones
const listShortUrlAliases = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/v1/short-url-aliases${qs ? `?${qs}` : ''}`);
};

// Create a new short URL alias in the pool (manual slug + optional display label)
const createShortUrlAlias = (slug, label) =>
    request('POST', '/v1/short-url-aliases', { slug, ...(label ? { label } : {}) });

// Auto-generate a unique short URL alias from the backend
const generateShortUrlAlias = () =>
    request('POST', '/v1/short-url-aliases/generate');

// Assign an alias to a session
const assignShortUrl = (slug, sessionId) =>
    request('POST', `/v1/short-url-aliases/${encodeURIComponent(slug)}/assign`, { sessionId });

// Unassign an alias from its session, returning it to the pool
const unassignShortUrl = (slug) =>
    request('DELETE', `/v1/short-url-aliases/${encodeURIComponent(slug)}/assign`);

// Permanently delete a short URL alias (works whether assigned or unassigned)
const deleteShortUrlAlias = (slug) =>
    request('DELETE', `/v1/short-url-aliases/${encodeURIComponent(slug)}`);

// Lock session
const lockSession = (sessionId, lockedToUrl) =>
    request('POST', `/v1/sessions/${sessionId}/lock`, { lockedToUrl });

// Enabled Maestro setup fields for the session create/edit form (global config)
const listSessionFields = () =>
    request('GET', '/v1/session-fields');

const sessionService = {
    listMySessions,
    listOrgSessions,
    getSessionById,
    createSession,
    updateSession,
    deleteSession,
    loadSession,
    unlockSession,
    listShortUrlAliases,
    createShortUrlAlias,
    generateShortUrlAlias,
    assignShortUrl,
    unassignShortUrl,
    deleteShortUrlAlias,
    lockSession,
    listSessionFields,
};

export default sessionService;
